import { useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { EyeIcon, TrashIcon, PencilIcon, CheckCircleIcon, XCircleIcon, UserCircleIcon, ExclamationCircleIcon } from "@heroicons/react/24/outline";
import { motion } from "framer-motion";
import axios from "axios";

const getStatusInfo = (statut) => {
  switch (statut) {
    case "Client":
      return { color: "bg-green-100 text-green-700", icon: <CheckCircleIcon className="w-5 h-5 text-green-500" /> };
    case "Sans Suite":
      return { color: "bg-red-100 text-red-700", icon: <XCircleIcon className="w-5 h-5 text-red-500" /> };
    case "Prospect Qualifié":
      return { color: "bg-blue-100 text-blue-700", icon: <UserCircleIcon className="w-5 h-5 text-blue-500" /> };
    case "Prospect":
      return { color: "bg-yellow-100 text-yellow-700", icon: <ExclamationCircleIcon className="w-5 h-5 text-yellow-500" /> };
    default:
      return { color: "bg-gray-100 text-gray-700", icon: <UserCircleIcon className="w-5 h-5 text-gray-500" /> };
  }
};

export default function LeadsGrid({ leads, selectProspect, onEdit, refreshLeads, selectedLeads, setSelectedLeads }) {
  const [confirmArchive, setConfirmArchive] = useState(null);
  const filteredLeads = leads.filter((lead) => lead.statut !== "Sans Suite");

  const BACKEND_URL =
    window.location.hostname === "localhost"
      ? process.env.REACT_APP_BACKEND_URL_LOCAL + "/api"
      : process.env.REACT_APP_BACKEND_URL_PROD + "/api";

  const handleArchive = async () => {
    if (!confirmArchive) return;
    try {
      await axios.put(`${BACKEND_URL}/leads/${confirmArchive._id}`, { statut: "Sans Suite" });
      refreshLeads();
    } catch (err) {
      console.error("Erreur lors de l'archivage :", err);
    }
    setConfirmArchive(null);
  };

  const columns = [
    { field: "type", headerName: "Type", width: 150, headerAlign: "center", align: "center" },
    { field: "nom", headerName: "Nom", width: 150, headerAlign: "center", align: "center" },
    { field: "prenom", headerName: "Prénom", width: 150, headerAlign: "center", align: "center" },
    { field: "email", headerName: "Email", width: 200, headerAlign: "center", align: "center" },
    { field: "telephone", headerName: "Téléphone", width: 170, headerAlign: "center", align: "center" },
    { field: "sourceAcquisition", headerName: "Source", width: 170, headerAlign: "center", align: "center" },
    { field: "commentaire", headerName: "Commentaires", width: 220, headerAlign: "center", align: "center" },
    {
      field: "statut",
      headerName: "Statut",
      width: 160,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const { color, icon } = getStatusInfo(params.value);
        return (
          <div className="flex items-center justify-center w-full h-full">
            <div className={`flex items-center gap-2 ${color} rounded-full px-3 py-1 text-sm font-semibold`}>
              {icon}
              {params.value}
            </div>
          </div>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <div className="flex items-center justify-center w-full h-full">
          <div className="flex gap-2">
            <button
              title="Afficher"
              className="p-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white transition"
              onClick={() => selectProspect(params.row)}
            >
              <EyeIcon className="w-5 h-5" />
            </button>
            <button
              title="Éditer"
              className="p-2 rounded-full bg-yellow-500 hover:bg-yellow-600 text-white transition"
              onClick={() => onEdit(params.row)}
            >
              <PencilIcon className="w-5 h-5" />
            </button>
            <button
              title="Archiver"
              className="p-2 rounded-full bg-red-600 hover:bg-red-700 text-white transition"
              onClick={() => setConfirmArchive(params.row)}
            >
              <TrashIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      ),
    },    
  ];

  return (
    <div className="bg-white p-4 shadow rounded-lg" style={{ height: "80vh", overflow: "auto" }}>
      <DataGrid
        rows={filteredLeads.map((lead) => ({
          ...lead,
          id: lead._id,
        }))}
        columns={columns}
        pageSize={5}
        rowsPerPageOptions={[5, 10, 20]}
        checkboxSelection
        selectionModel={selectedLeads}
        onSelectionModelChange={(newSelection) => {
          setSelectedLeads(newSelection);
          console.log("Sélection en cours :", newSelection);
        }}
      />

      {confirmArchive && (
        <motion.div
          className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="bg-white p-6 rounded-lg shadow-lg w-[400px]"
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0.8 }}
          >
            <h2 className="text-lg font-semibold text-center">Archiver ce lead ?</h2>
            <p className="text-gray-600 text-center mt-2">
              Êtes-vous sûr de vouloir archiver <strong>{confirmArchive.nom} {confirmArchive.prenom}</strong> ?
            </p>
            <div className="flex justify-between mt-4">
              <button
                className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-lg"
                onClick={() => setConfirmArchive(null)}
              >
                Annuler
              </button>
              <button
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg"
                onClick={handleArchive}
              >
                Confirmer
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
}
